import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { isBrowser } from 'ws-scripts/modules/environment';
import { usePrefs, useFlags } from 'wsm-common-data';

import vehiclePropType from '../../../propTypes/vehiclePropType';
import { selectIsListLayout } from '../../../utilities/layout';
import DetailsSkeleton, {
	DetailsSkeletonContainerClasses,
	DetailsSkeletonHeightParityClasses
} from '../skeleton/DetailsSkeleton';
import TitleSkeleton from '../skeleton/TitleSkeleton';
import VehicleTitle from './VehicleTitle';
import Details from './Details';
import { safeGetItem } from '../../../utilities/safeStorage';
import {
	SHOW_PAYMENTS_NOTIFICATION,
	SHOW_PAYMENTS_LOCAL_STORAGE_KEY
} from '../../../global-constants';

const DetailsContainer = ({ shouldFullyRenderCard, ...props }) => {
	const [isDisplayingEPrice, setIsDisplayingEPrice] = useState(false);
	const [showExpandedPricing, setShowExpandedPricing] = useState(false); // todo pref sets initial state to show all pricing when configured
	const [isNewCarBoostStyle, setIsNewCarBoostStyle] = useState(false);

	const isListLayout = useSelector((state) => selectIsListLayout(state));
	const { showInformationTabs } = usePrefs();
	const flags = useFlags();
	// `ws-inv-listing-cls-media-stack-fix` gates all of the US2092262 CLS work, not
	// just the media stack it is named after — the changes are measured and rolled
	// out together.
	const isClsFixEnabled = flags['ws-inv-listing-cls-media-stack-fix'];
	const skeletonContainerClasses = isClsFixEnabled
		? DetailsSkeletonHeightParityClasses
		: DetailsSkeletonContainerClasses;
	const isNewCarBoost = props.vehicle?.isNewCarBoost;

	useEffect(() => {
		// Retrieve the user showing payments from local storage
		// Setting new car boost style when page load
		let userShowPayments;
		try {
			userShowPayments = JSON.parse(
				safeGetItem('localStorage', SHOW_PAYMENTS_LOCAL_STORAGE_KEY)
			);
		} catch (e) {
			// Malformed stored value - ignore and fall back to no preference.
		}
		setIsNewCarBoostStyle(userShowPayments?.show && isNewCarBoost);

		// Setting new car boost style based on the 'show-payments-notification' event
		let subscriber = null;
		const { pubsub } = (isBrowser && window.DDC) || {};
		if (pubsub) {
			const handleNotification = (notification) => {
				const { show } = notification;
				setIsNewCarBoostStyle(show && isNewCarBoost);
			};

			subscriber = pubsub.subscribe(
				SHOW_PAYMENTS_NOTIFICATION,
				handleNotification
			);
		}
		// Cleanup subscription on component unmount
		return () => {
			subscriber?.unsubscribe();
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	let newCarBoostPlacement;
	if (isNewCarBoostStyle) {
		newCarBoostPlacement =
			!isListLayout &&
			!showInformationTabs?.toLowerCase().includes('pricing')
				? 'title'
				: 'details';
	}

	return (
		<div
			className={`vehicle-card-details-container${
				!shouldFullyRenderCard
					? ` ${skeletonContainerClasses.join(' ')}`
					: ''
			}`}
		>
			{props.vehicle && !props.vehicle.isPlaceholder ? (
				<VehicleTitle
					title={props.vehicle.title}
					supplementalTitle={props.vehicle.supplementalTitle}
					link={props.vehicle.link}
					vehicleId={props.vehicle.uuid}
					vin={props.vehicle.vin}
					condition={props.vehicle.condition}
					certified={props.vehicle.certified}
					pricing={props.vehicle.pricing || {}}
					isNewCarBoost={props.vehicle.isNewCarBoost}
					isDisplayingEPrice={isDisplayingEPrice}
					accountInfo={{ ...props.accounts[props.vehicle.accountId] }}
					showExpandedPricing={showExpandedPricing}
					setShowExpandedPricing={setShowExpandedPricing}
					showNewCarBoostPaymentsInTitle={
						newCarBoostPlacement === 'title'
					}
				/>
			) : (
				<TitleSkeleton />
			)}

			{shouldFullyRenderCard ? (
				<Details
					{...props}
					isDisplayingEPrice={isDisplayingEPrice}
					setIsDisplayingEPrice={setIsDisplayingEPrice}
					showExpandedPricing={showExpandedPricing}
					setShowExpandedPricing={setShowExpandedPricing}
					showNewCarBoostPaymentsInDetails={
						newCarBoostPlacement === 'details'
					}
				/>
			) : (
				<DetailsSkeleton />
			)}
		</div>
	);
};

DetailsContainer.propTypes = {
	vehicle: vehiclePropType,
	accounts: PropTypes.shape({}),
	isListLayout: PropTypes.bool,
	shouldFullyRenderCard: PropTypes.bool.isRequired
};

export default DetailsContainer;
